// ===========================================
// HOOK: Notificaciones de actividad en Tareas (ORR-54)
// ===========================================
/**
 * Encapsula la lógica para notificar a los interesados de una tarea
 * cuando ocurre actividad (comentario, subtarea, recordatorio, adjunto).
 *
 * Reglas:
 * - Un comentario notifica al asignado, a quien asignó y a quien creó la tarea.
 * - El resto de la actividad: si el actor NO es el asignado → notificar al asignado.
 * - El resto de la actividad: si el actor ES el asignado (o la tarea no tiene asignado) → notificar al asignador.
 * - Nunca auto-notificar (actor === destinatario).
 * - Best-effort: los errores se loggean pero no rompen la mutación principal.
 *
 * ORR-69: migrado al metodo notificar() unificado con origen trazable.
 */

import { useCallback } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { notificacionesService } from '../services/notificacionesService'
import { PLANTILLAS_NOTIFICACION } from '../types/notificaciones'
import type { DestinatarioSpec, NotificacionOrigen } from '../types/notificaciones'

interface TareaParaNotificar {
  id: string
  titulo: string
  asignado_a: string | null
  asignado_por: string | null
  created_by?: string | null
}

type TipoActividad =
  | { tipo: 'comentario' }
  | { tipo: 'subtarea'; tituloSubtarea: string }
  | { tipo: 'recordatorio' }
  | { tipo: 'adjunto'; nombreArchivo: string }

export function useNotificacionesTarea() {
  const { user } = useAuth0()
  const { supabase } = useSupabase()

  const notificarActividad = useCallback(
    async (tarea: TareaParaNotificar, actividad: TipoActividad) => {
      if (!supabase) return

      const userEmail = user?.email
      const autor = user?.name || user?.email || 'Alguien'
      if (!userEmail) return

      // Decidir destinatarios únicos
      const destinatarios = new Set<string>()

      if (actividad.tipo === 'comentario') {
        for (const email of [tarea.asignado_a, tarea.asignado_por, tarea.created_by]) {
          if (email && email !== userEmail) destinatarios.add(email)
        }
      } else {
        // Notificar al asignado si el actor no es el asignado
        if (tarea.asignado_a && tarea.asignado_a !== userEmail) {
          destinatarios.add(tarea.asignado_a)
        }

        // Notificar al asignador si el actor es el asignado (o no hay asignado)
        if (
          tarea.asignado_por &&
          tarea.asignado_por !== userEmail &&
          (tarea.asignado_a === userEmail || !tarea.asignado_a)
        ) {
          destinatarios.add(tarea.asignado_por)
        }
      }

      if (destinatarios.size === 0) return

      // Armar plantilla segun tipo de actividad (el tipo de la notif siempre es 'tarea')
      let plantilla: { titulo: string; mensaje: string }
      let tablaFuente: string
      switch (actividad.tipo) {
        case 'comentario':
          plantilla = PLANTILLAS_NOTIFICACION.tareaNuevoComentario(tarea.titulo, autor)
          tablaFuente = 'comentarios_tarea'
          break
        case 'subtarea':
          plantilla = PLANTILLAS_NOTIFICACION.tareaNuevaSubtarea(
            tarea.titulo,
            autor,
            actividad.tituloSubtarea,
          )
          tablaFuente = 'tarea_subtareas'
          break
        case 'recordatorio':
          plantilla = PLANTILLAS_NOTIFICACION.tareaNuevoRecordatorio(tarea.titulo, autor)
          tablaFuente = 'tarea_recordatorios'
          break
        case 'adjunto':
          plantilla = PLANTILLAS_NOTIFICACION.tareaNuevoAdjunto(
            tarea.titulo,
            autor,
            actividad.nombreArchivo,
          )
          tablaFuente = 'tareas'
          break
      }

      const specs: DestinatarioSpec[] = [...destinatarios].map((email) => `user:${email}`)

      const origen: NotificacionOrigen = {
        canal: 'web',
        actor_email: userEmail,
        tabla_fuente: tablaFuente,
        registro_id: tarea.id,
      }

      try {
        await notificacionesService.notificar(supabase, {
          destinatarios: specs,
          titulo: plantilla.titulo,
          mensaje: plantilla.mensaje,
          tipo: 'tarea',
          enlace: `/tareas?id=${tarea.id}`,
          origen,
        })
      } catch (err) {
        console.error('Error notificando actividad de tarea:', err)
      }
    },
    [supabase, user?.email, user?.name],
  )

  return { notificarActividad }
}
